import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DataObjectIcon from "@mui/icons-material/DataObject";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  BKGAvailableSource,
  WorkspaceIntegrationSelection,
} from "../../../common/types";
import {
  useCreateWorkspaceIntegrationSelection,
  useDeleteWorkspaceIntegrationSelection,
  useListBKGAvailableSources,
  useUpdateWorkspaceIntegrationSelection,
} from "../../../services/api";

const EMPTY_STATE_COPY =
  "No BKG workspaces in your organization that you have access to. Ask an organization admin to enable BKG on a workspace and add you to it.";

const SOURCE_UNAVAILABLE_COPY =
  "This BKG source is no longer accessible. Re-configure from Workspace Settings → Integrations → BKG.";

export const getBKGSourcePickerErrorMessage = (error: any): string => {
  const detail = error?.response?.detail;
  const detailCode = typeof detail === "object" ? detail?.code : undefined;
  const detailMessage = typeof detail === "object" ? detail?.message : detail;

  if (detailCode === "source_unavailable") {
    return SOURCE_UNAVAILABLE_COPY;
  }

  return (
    detailMessage ||
    error?.message ||
    "Failed to update the BKG source. Refresh and try again."
  );
};

const sourceIdFromSelection = (
  selection?: WorkspaceIntegrationSelection | null,
) =>
  selection?.source_workspace_id ||
  selection?.workspace_config?.source_workspace_id ||
  null;

interface BKGSourcePickerProps {
  open: boolean;
  workspaceId: string;
  organizationIntegrationId: string;
  selection?: WorkspaceIntegrationSelection | null;
  onClose: () => void;
  onSelectionChanged: (
    selection: WorkspaceIntegrationSelection | null,
  ) => Promise<void> | void;
}

const BKGSourcePicker: React.FC<BKGSourcePickerProps> = ({
  open,
  workspaceId,
  organizationIntegrationId,
  selection,
  onClose,
  onSelectionChanged,
}) => {
  const [sources, setSources] = useState<BKGAvailableSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingSourceId, setSavingSourceId] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [currentSelection, setCurrentSelection] =
    useState<WorkspaceIntegrationSelection | null>(selection || null);

  const listSources = useListBKGAvailableSources();
  const createSelection = useCreateWorkspaceIntegrationSelection();
  const updateSelection = useUpdateWorkspaceIntegrationSelection();
  const deleteSelection = useDeleteWorkspaceIntegrationSelection();

  useEffect(() => {
    if (open) {
      setCurrentSelection(selection || null);
      setChanging(!sourceIdFromSelection(selection));
      setError(null);
    }
  }, [open, selection]);

  const loadSources = useCallback(async () => {
    if (!open || !workspaceId) return;

    try {
      setLoading(true);
      setError(null);
      const nextSources = await listSources(workspaceId);
      setSources(nextSources);
    } catch (loadError: any) {
      setError(getBKGSourcePickerErrorMessage(loadError));
      setSources([]);
    } finally {
      setLoading(false);
    }
  }, [listSources, open, workspaceId]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  const selectedSourceId = sourceIdFromSelection(currentSelection);
  const selectedSource = useMemo(() => {
    if (!selectedSourceId) return null;
    return (
      sources.find((source) => source.workspace_id === selectedSourceId) || {
        workspace_id: selectedSourceId,
        name:
          currentSelection?.source_workspace_name ||
          currentSelection?.workspace_config?.source_workspace_name ||
          selectedSourceId,
      }
    );
  }, [currentSelection, selectedSourceId, sources]);

  const handleSelectSource = async (source: BKGAvailableSource) => {
    try {
      setSavingSourceId(source.workspace_id);
      setError(null);
      const nextSelection = currentSelection
        ? await updateSelection(workspaceId, currentSelection.id, {
            source_workspace_id: source.workspace_id,
            enabled: true,
          })
        : await createSelection(workspaceId, {
            organization_integration_id: organizationIntegrationId,
            workspace_config: {},
            source_workspace_id: source.workspace_id,
            enabled: true,
          });

      const hydratedSelection = {
        ...nextSelection,
        source_workspace_id:
          nextSelection.source_workspace_id || source.workspace_id,
        source_workspace_name:
          nextSelection.source_workspace_name || source.name,
      };
      setCurrentSelection(hydratedSelection);
      setChanging(false);
      await onSelectionChanged(hydratedSelection);
    } catch (saveError: any) {
      setError(getBKGSourcePickerErrorMessage(saveError));
    } finally {
      setSavingSourceId(null);
    }
  };

  const handleRemoveSource = async () => {
    if (!currentSelection) return;

    try {
      setRemoving(true);
      setError(null);
      await deleteSelection(workspaceId, currentSelection.id);
      setCurrentSelection(null);
      setChanging(true);
      await onSelectionChanged(null);
    } catch (removeError: any) {
      setError(getBKGSourcePickerErrorMessage(removeError));
    } finally {
      setRemoving(false);
    }
  };

  const showSourceList = changing || !selectedSource;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: "min(720px, calc(100vw - 32px))" }}>
        <DialogTitle>Configure BKG source</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography level="body-sm" color="neutral">
              Select the BKG-enabled workspace this workspace should read from.
              Only workspaces in this organization that you can access are
              shown.
            </Typography>

            {error && (
              <Alert color="danger" variant="soft">
                {error}
              </Alert>
            )}

            {selectedSource && (
              <Card variant="soft" color="primary">
                <CardContent>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <CheckCircleIcon color="primary" />
                      <Box>
                        <Typography level="title-md">
                          {selectedSource.name}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          Current BKG source workspace
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        startDecorator={<SwapHorizIcon />}
                        onClick={() => setChanging(true)}
                        disabled={removing || Boolean(savingSourceId)}
                      >
                        Change
                      </Button>
                      <Button
                        size="sm"
                        variant="outlined"
                        color="danger"
                        startDecorator={<DeleteIcon />}
                        onClick={handleRemoveSource}
                        loading={removing}
                        disabled={Boolean(savingSourceId)}
                      >
                        Remove
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {showSourceList && (
              <Stack spacing={1}>
                <Typography level="title-sm">
                  Available BKG workspaces
                </Typography>
                {loading ? (
                  <Box sx={{ py: 3, textAlign: "center" }}>
                    <CircularProgress />
                  </Box>
                ) : sources.length === 0 ? (
                  <Alert color="neutral" variant="soft">
                    {EMPTY_STATE_COPY}
                  </Alert>
                ) : (
                  sources.map((source) => {
                    const isSelected = selectedSourceId === source.workspace_id;
                    return (
                      <Card key={source.workspace_id} variant="outlined">
                        <CardContent>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            justifyContent="space-between"
                            alignItems={{ xs: "stretch", sm: "center" }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <DataObjectIcon color="primary" />
                              <Box>
                                <Typography level="title-md">
                                  {source.name}
                                </Typography>
                                <Typography level="body-xs" color="neutral">
                                  {source.workspace_id}
                                </Typography>
                              </Box>
                            </Stack>
                            <Button
                              size="sm"
                              variant={isSelected ? "soft" : "solid"}
                              color="primary"
                              onClick={() => handleSelectSource(source)}
                              loading={savingSourceId === source.workspace_id}
                              disabled={removing || Boolean(savingSourceId)}
                            >
                              {isSelected ? "Selected" : "Select"}
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="solid" color="primary" onClick={onClose}>
            Done
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default BKGSourcePicker;
